import {
  getStorage,
  ref,
  uploadBytesResumable,
  getDownloadURL,
} from "firebase/storage";

// Original FileUpload function for backward compatibility
export const FileUpload = ({ onError, onSuccess, file }: any) => {
  const storage = getStorage();
  const storageRef = ref(storage, `products/${file.name}`);
  const metadata = {
    contentType: file.type,
  };
  const uploadTask = uploadBytesResumable(storageRef, file, metadata);
  uploadTask.on(
    "state_changed",
    (snapshot) => {
      const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
      console.log("Upload is " + progress + "% done");
      switch (snapshot.state) {
        case "paused":
          console.log("Upload is paused");
          break;
        case "running":
          console.log("Upload is running");
          break;
      }
    },
    (error) => {
      onError(error);
    },
    () => {
      getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
        onSuccess(downloadURL);
      });
    }
  );
};

/**
 * Upload a single file to Firebase Storage and return its download URL
 * @param file - The file to upload
 * @param folder - The storage folder path (e.g., 'stores/logos', 'stores/images')
 * @returns Promise<string> - The download URL of the uploaded file
 * @throws Error with user-friendly message if upload fails
 */
export const uploadSingleFile = async (
  file: File,
  folder: string
): Promise<string> => {
  try {
    const storage = getStorage();
    // Create a unique filename using timestamp to prevent collisions
    const timestamp = Date.now();
    const sanitizedName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
    const fileName = `${timestamp}_${sanitizedName}`;
    const storageRef = ref(storage, `${folder}/${fileName}`);

    const metadata = {
      contentType: file.type,
    };

    // Upload file
    const uploadTask = uploadBytesResumable(storageRef, file, metadata);

    // Wait for upload to complete
    await new Promise<void>((resolve, reject) => {
      uploadTask.on(
        "state_changed",
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          console.log(`Uploading ${file.name}: ${Math.round(progress)}% done`);
        },
        (error) => {
          console.error("Upload error:", error);
          reject(new Error(`Failed to upload ${file.name}. Please check your internet connection and try again.`));
        },
        () => {
          resolve();
        }
      );
    });

    // Get download URL
    const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
    return downloadURL;
  } catch (error: any) {
    console.error("File upload error:", error);
    throw new Error(error.message || `Failed to upload ${file.name}. Please try again.`);
  }
};

/**
 * Upload multiple files to Firebase Storage in parallel
 * @param files - Array of files to upload
 * @param folder - The storage folder path (e.g., 'stores/images')
 * @returns Promise<string[]> - Array of download URLs in same order as input files
 * @throws Error with details if any upload fails
 */
export const uploadMultipleFiles = async (
  files: File[],
  folder: string
): Promise<string[]> => {
  try {
    // Upload all files in parallel for better performance
    const uploadPromises = files.map(file => uploadSingleFile(file, folder));
    const urls = await Promise.all(uploadPromises);
    return urls;
  } catch (error: any) {
    console.error("Multiple file upload error:", error);
    throw new Error(error.message || "Failed to upload one or more files. Please try again.");
  }
};

/**
 * Upload all registration form files (avatar, store images, product images)
 * @param formData - The registration form data containing File objects
 * @returns Promise with URLs for avatar, images array, and products array with image URLs
 * @throws Error with user-friendly message if any upload fails
 */
export const uploadRegistrationFiles = async (formData: {
  avatar: File | null;
  images: File[];
  products: Array<{ description: string; image: File | null }>;
}): Promise<{
  avatarUrl: string | null;
  imageUrls: string[];
  productUrls: Array<{ description: string; imageUrl: string | null }>;
}> => {
  try {
    console.log("Starting file uploads for registration...");

    // Upload avatar (store logo)
    let avatarUrl: string | null = null;
    if (formData.avatar) {
      console.log("Uploading store logo...");
      avatarUrl = await uploadSingleFile(formData.avatar, "stores/logos");
      console.log("Store logo uploaded successfully");
    }

    // Upload store images
    console.log(`Uploading ${formData.images.length} store images...`);
    const imageUrls = await uploadMultipleFiles(formData.images, "stores/images");
    console.log("Store images uploaded successfully");

    // Upload product images
    console.log(`Uploading ${formData.products.length} product images...`);
    const productUrls = await Promise.all(
      formData.products.map(async (product) => {
        if (product.image) {
          const imageUrl = await uploadSingleFile(product.image, "stores/products");
          return { description: product.description, imageUrl };
        }
        return { description: product.description, imageUrl: null };
      })
    );
    console.log("Product images uploaded successfully");

    console.log("All files uploaded successfully");
    return { avatarUrl, imageUrls, productUrls };
  } catch (error: any) {
    console.error("Registration file upload error:", error);
    throw new Error(
      error.message ||
      "Failed to upload files. Please check your internet connection and ensure all files are valid images (PNG, JPG, GIF) under 10MB."
    );
  }
};
